import * as Sentry from '@sentry/react';
import { getAnalytics, isSupported, logEvent } from 'firebase/analytics';
import { getApps, initializeApp } from 'firebase/app';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const hasFirebaseConfig = Object.values(firebaseConfig).every(Boolean);
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: process.env.NODE_ENV,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
  });
}

if (!hasFirebaseConfig && process.env.NODE_ENV !== 'production') {
  console.info('Firebase Analytics is disabled until its REACT_APP_FIREBASE_* settings are configured.');
}

const analyticsPromise = hasFirebaseConfig
  ? isSupported()
    .then((supported) => {
      if (!supported) return null;
      const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
      return getAnalytics(app);
    })
    .catch((error) => {
      console.warn('Firebase Analytics could not initialize:', error);
      return null;
    })
  : Promise.resolve(null);

export function trackAnalyticsEvent(eventName, parameters = {}) {
  return analyticsPromise.then((analytics) => {
    if (!analytics) return;

    const safeParameters = Object.fromEntries(
      Object.entries(parameters).filter(([, value]) => (
        typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      )),
    );

    logEvent(analytics, eventName, safeParameters);
  }).catch((error) => {
    console.warn(`Firebase Analytics event "${eventName}" could not be recorded:`, error);
  });
}

export function reportClientError(source, error) {
  trackAnalyticsEvent('client_error', {
    source,
    error_type: error instanceof Error ? error.name : typeof error,
  });
}

const originalFetch = window.fetch.bind(window);
const apiBaseUrl = new URL(API_BASE_URL, window.location.origin);

window.fetch = async (input, init) => {
  const requestUrl = input instanceof Request ? input.url : String(input);
  const url = new URL(requestUrl, window.location.origin);
  const isApiRequest = url.origin === apiBaseUrl.origin
    && (url.pathname === apiBaseUrl.pathname || url.pathname.startsWith(`${apiBaseUrl.pathname}/`));

  if (!isApiRequest) {
    return originalFetch(input, init);
  }

  const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
  const endpoint = url.pathname.slice(apiBaseUrl.pathname.length).slice(0, 80) || '/';
  const reportFailure = (failureType, statusCode) => {
    trackAnalyticsEvent('api_error', {
      endpoint,
      method,
      failure_type: failureType,
      ...(statusCode ? { status_code: statusCode } : {}),
    });

    if (failureType === 'network' || statusCode >= 500) {
      const error = new Error(`API ${method} ${endpoint} failed${statusCode ? ` with HTTP ${statusCode}` : ''}.`);
      Sentry.captureException(error, {
        tags: { error_source: 'api', api_endpoint: endpoint, api_method: method },
        extra: statusCode ? { statusCode } : undefined,
      });
    }
  };

  try {
    const response = await originalFetch(input, init);
    if (response.status === 401) {
      response.clone().json().then((data) => {
        if (data.code === 'USER_ACCOUNT_DELETED') {
          window.dispatchEvent(new Event('cd:user-account-deleted'));
        }
      }).catch(() => undefined);
    }
    if (!response.ok) {
      reportFailure('http', response.status);
    }
    return response;
  } catch (error) {
    reportFailure('network');
    throw error;
  }
};

window.addEventListener('error', (event) => {
  reportClientError('uncaught_error', event.error);
});

window.addEventListener('unhandledrejection', (event) => {
  reportClientError('unhandled_rejection', event.reason);
});

export { Sentry };
